import * as SheetPrimitive from '@radix-ui/react-dialog';
import { X } from 'lucide-react';
import * as React from 'react';

import { Button } from '@/components/ui/button';
import { overlayClass } from '@/components/ui/dialog';
import { cn } from '@/lib/utils';

/**
 * Panneau latéral (fiche) shadcn/ui au système Voxler Router
 * (UseCaseSheet.dc.html) : à droite, 600px, fond `popover`, filet à gauche,
 * ombre shadow-md ; `SheetHead` (titre 20px + badge, sous-titre, fermer),
 * `SheetBody` défilant (sections espacées de 24px), `SheetFooter` à filet.
 */

const Sheet = SheetPrimitive.Root;
const SheetTrigger = SheetPrimitive.Trigger;
const SheetClose = SheetPrimitive.Close;
const SheetPortal = SheetPrimitive.Portal;

const SheetOverlay = React.forwardRef<
  React.ElementRef<typeof SheetPrimitive.Overlay>,
  React.ComponentPropsWithoutRef<typeof SheetPrimitive.Overlay>
>(({ className, ...props }, ref) => (
  <SheetPrimitive.Overlay className={cn(overlayClass, className)} {...props} ref={ref} />
));
SheetOverlay.displayName = SheetPrimitive.Overlay.displayName;

interface SheetContentProps extends React.ComponentPropsWithoutRef<typeof SheetPrimitive.Content> {
  /** Largeur en pixels (600 par défaut). */
  width?: number;
}

const SheetContent = React.forwardRef<
  React.ElementRef<typeof SheetPrimitive.Content>,
  SheetContentProps
>(({ className, children, width = 600, style, ...props }, ref) => (
  <SheetPortal>
    <SheetOverlay />
    <SheetPrimitive.Content
      ref={ref}
      style={{ width: `min(${width}px, 100vw)`, ...style }}
      className={cn(
        'fixed inset-y-0 right-0 z-50 flex h-full flex-col border-l border-border bg-popover text-popover-foreground shadow-md',
        'transition ease-in-out data-[state=closed]:duration-200 data-[state=open]:duration-300 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:slide-out-to-right data-[state=open]:slide-in-from-right',
        'focus:outline-none',
        className,
      )}
      {...props}
    >
      {children}
    </SheetPrimitive.Content>
  </SheetPortal>
));
SheetContent.displayName = SheetPrimitive.Content.displayName;

/** Conteneur d'en-tête libre (compatibilité shadcn). Préférer `SheetHead`. */
function SheetHeader({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>): React.JSX.Element {
  return <div className={cn('flex flex-col gap-0.5 px-6 pb-3 pt-6', className)} {...props} />;
}

interface SheetHeadProps extends Omit<React.HTMLAttributes<HTMLDivElement>, 'title'> {
  title: React.ReactNode;
  /** À droite du titre : badge de statut. */
  badge?: React.ReactNode;
  description?: React.ReactNode;
  /** Titre en mono (clé technique). */
  mono?: boolean;
  closeLabel?: string;
}

/** `sheet-head` : titre (20px) + badge, sous-titre, bouton fermer. */
function SheetHead({
  title,
  badge,
  description,
  mono = false,
  closeLabel = 'Fermer la fiche',
  className,
  children,
  ...props
}: SheetHeadProps): React.JSX.Element {
  return (
    <div className={cn('shrink-0 px-6 pb-3 pt-6', className)} {...props}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <SheetTitle className={cn(mono && 'font-mono')}>{title}</SheetTitle>
            {badge}
          </div>
          {description !== undefined && description !== null && description !== '' ? (
            <SheetDescription className="mt-0.5">{description}</SheetDescription>
          ) : null}
        </div>
        <SheetClose asChild>
          <Button variant="ghost" size="icon-sm" aria-label={closeLabel}>
            <X aria-hidden="true" />
          </Button>
        </SheetClose>
      </div>
      {children !== undefined && children !== null ? <div className="mt-3">{children}</div> : null}
    </div>
  );
}

/** `sheet-body` : contenu défilant, sections espacées de 24px. */
function SheetBody({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>): React.JSX.Element {
  return (
    <div
      className={cn(
        'grid min-h-0 flex-1 content-start gap-6 overflow-y-auto px-6 pb-6 pt-4',
        className,
      )}
      {...props}
    />
  );
}

/** Pied de fiche (`dialog-foot`) : actions à droite, `mr-auto` pour en pousser une à gauche. */
function SheetFooter({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>): React.JSX.Element {
  return (
    <div
      className={cn(
        'flex shrink-0 flex-wrap items-center justify-end gap-2 border-t border-border px-6 py-3',
        className,
      )}
      {...props}
    />
  );
}

const SheetTitle = React.forwardRef<
  React.ElementRef<typeof SheetPrimitive.Title>,
  React.ComponentPropsWithoutRef<typeof SheetPrimitive.Title>
>(({ className, ...props }, ref) => (
  <SheetPrimitive.Title ref={ref} className={cn('m-0 text-title', className)} {...props} />
));
SheetTitle.displayName = SheetPrimitive.Title.displayName;

const SheetDescription = React.forwardRef<
  React.ElementRef<typeof SheetPrimitive.Description>,
  React.ComponentPropsWithoutRef<typeof SheetPrimitive.Description>
>(({ className, ...props }, ref) => (
  <SheetPrimitive.Description
    ref={ref}
    className={cn('m-0 text-[13px] leading-[18px] text-muted-foreground', className)}
    {...props}
  />
));
SheetDescription.displayName = SheetPrimitive.Description.displayName;

export {
  Sheet,
  SheetPortal,
  SheetOverlay,
  SheetTrigger,
  SheetClose,
  SheetContent,
  SheetHeader,
  SheetHead,
  SheetBody,
  SheetFooter,
  SheetTitle,
  SheetDescription,
};
